import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAIFormCheckVisibility } from '../hooks/useAIFormCheckVisibility';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from './ui/sheet';
import {
  Menu,
  Home,
  Sparkles,
  CalendarDays,
  Layers,
  Baby,
  Users,
  Zap,
  Ticket,
  Coins,
  Wallet as WalletIcon,
  User as UserIcon,
  LogIn,
  UserPlus,
  LogOut,
  ChevronRight,
  Award,
  Compass,
  Laptop,
  UserCheck,
  MapPin,
  Package,
  Trophy,
  Phone,
  ScanLine,
  GitCompareArrows,
  Flag,
} from 'lucide-react';
import { saveReturnTo } from '../lib/returnTo';
import { navigateExplore } from './ExploreLGRAMenu';

/**
 * MobileNavDrawer — mobile-only ☰ menu that gives customers one-tap access
 * to every public LGRA section from ANY page in the customer journey.
 *
 * Drawer sections (Feb 25 2026 restructure to align with the desktop
 * "Explore LGRA" dropdown):
 *   ┌ Start Here          — Home · Find My Best Training Option · How Would You Like to Train?
 *   ├ Training Options    — Physical Classes · Online Training Plan · Private Coaching
 *   ├ Explore LGRA        — Locations · Class Packages · Student Stories · Contact LGRA
 *   ├ Full Site           — Class Schedule · All Classes · Coaches · Membership
 *   ├ Classes & Purchase  — category filters + walk-in + packages
 *   └ My LGRA             — signed-in customer entry points (bookings, wallet, …)
 *
 * The first three groups are the same 10 shortcuts used by the desktop
 * dropdown; they use `navigateExplore` so behaviour is identical (same-page
 * smooth-scroll, cross-page anchored navigation).
 */

// ── Explore LGRA anchor groups (in-sync with ExploreLGRAMenu.jsx) ───────
const START_HERE = [
  { label: 'Home', to: '/#top', testId: 'mnav-explore-home', Icon: Home },
  { label: 'Find My Best Training Plan', to: '/online-training-plan', testId: 'mnav-explore-find-my-class', Icon: Sparkles },
  { label: 'Choose Your Training Path', to: '/#training-options', testId: 'mnav-explore-training-options', Icon: Compass },
  { label: 'Compare Training Options', to: '/compare-training-options', testId: 'mnav-explore-compare-training', Icon: GitCompareArrows },
];
const TRAINING_OPTIONS = [
  { label: 'Running Classes', to: '/running-classes', testId: 'mnav-explore-physical-classes', Icon: Users },
  { label: 'Online Training Plan', to: '/online-training-plan', testId: 'mnav-explore-online-training-plan', Icon: Laptop },
  { label: 'Private Coaching', to: '/coaching/private', testId: 'mnav-explore-private-coaching', Icon: UserCheck },
  { label: 'AI Running Form Check', to: '/ai-running-form-check', testId: 'mnav-explore-ai-form-check', Icon: ScanLine, badge: 'NEW' },
];
const EXPLORE_MORE = [
  { label: 'Training Locations', to: '/#locations', testId: 'mnav-explore-locations', Icon: MapPin },
  { label: 'Membership & Packages', to: '/packages', testId: 'mnav-explore-packages', Icon: Package },
  { label: 'Runners’ Stories', to: '/runners-stories', testId: 'mnav-explore-student-stories', Icon: Trophy },
  { label: 'Contact LGRA', to: '/#contact', testId: 'mnav-explore-contact', Icon: Phone },
];

// ── Full page shortcuts (unchanged from previous drawer version) ────────
const SITE_ITEMS = [
  { to: '/schedule', label: 'Class Schedule', icon: CalendarDays, testId: 'mobile-nav-schedule', match: (p) => p.startsWith('/schedule') },
  { to: '/all-classes', label: 'All Classes', icon: Layers, testId: 'mobile-nav-all-classes', match: (p) => p === '/all-classes' || p.startsWith('/class/') },
  { to: '/race-calendar', label: 'Upcoming Races', icon: Flag, testId: 'mobile-nav-races', match: (p) => p.startsWith('/race-calendar') || p.startsWith('/find-my-race') },
  { to: '/runner-market', label: 'Runner Market', icon: Package, testId: 'mobile-nav-runner-market', match: (p) => p.startsWith('/runner-market') },
  { to: '/coaches', label: 'Coaches', icon: Award, testId: 'mobile-nav-coaches', match: (p) => p.startsWith('/coaches') },
  { to: '/membership', label: 'Membership', icon: UserIcon, testId: 'mobile-nav-membership', match: (p) => p === '/membership' },
];

const CATEGORY_ITEMS = [
  { to: '/all-classes?category=kids', label: 'Kids & Teens', icon: Baby, testId: 'mobile-nav-cat-kids-teens', matchKeys: ['kids', 'teens'] },
  { to: '/all-classes?category=adult_physical', label: 'Adult Group Classes', icon: Users, testId: 'mobile-nav-cat-adult', matchKeys: ['adult_physical'] },
  { to: '/all-classes?category=enhancement', label: 'Enhancement Classes', icon: Zap, testId: 'mobile-nav-cat-enhancement', matchKeys: ['enhancement'] },
  { to: '/walk-in', label: 'Trial / Walk-In', icon: Ticket, testId: 'mobile-nav-walk-in', matchKeys: ['walk-in', 'trials'] },
  { to: '/packages', label: 'Packages & Credits', icon: Coins, testId: 'mobile-nav-packages', matchKeys: ['packages'] },
];

const AUTH_ITEMS = [
  { to: '/my-bookings', label: 'My Bookings', icon: CalendarDays, testId: 'mobile-nav-my-bookings' },
  { to: '/my-classes-recordings', label: 'My Classes & Recordings', icon: CalendarDays, testId: 'mobile-nav-my-classes-recordings' },
  { to: '/my-races', label: 'My Races', icon: Flag, testId: 'mobile-nav-my-races' },
  { to: '/packages?tab=balance', label: 'My Packages / Balance', icon: Layers, testId: 'mobile-nav-my-balance' },
  { to: '/my-wallet', label: 'LGRA Wallet', icon: WalletIcon, testId: 'mobile-nav-my-wallet' },
  { to: '/profile', label: 'My Profile', icon: UserIcon, testId: 'mobile-nav-my-profile' },
];

const isCategoryActive = (pathname, search, keys) => {
  if (!pathname.startsWith('/all-classes')) {
    if (keys.includes('walk-in') && (pathname.startsWith('/walk-in') || pathname.startsWith('/trials'))) return true;
    if (keys.includes('packages') && pathname.startsWith('/packages')) return true;
    return false;
  }
  const params = new URLSearchParams(search);
  const cat = params.get('category');
  return !!cat && keys.includes(cat);
};

const NavRow = ({ to, icon: Icon, label, active, onClick, testId }) => (
  <Link
    to={to}
    onClick={onClick}
    className={`flex items-center gap-3 px-4 min-h-[52px] rounded-lg transition-colors border ${
      active
        ? 'bg-[#F5F3FF] border-[#D8C4FF] text-[#3F2774]'
        : 'bg-white border-transparent text-slate-800 hover:bg-slate-50 hover:border-slate-200'
    }`}
    data-testid={testId}
    aria-current={active ? 'page' : undefined}
  >
    <span
      className={`shrink-0 w-9 h-9 rounded-full border flex items-center justify-center ${
        active ? 'bg-white border-[#D8C4FF] text-[#7E22CE]' : 'bg-[#FBF9FF] border-[#EDE9FE] text-[#7E22CE]'
      }`}
    >
      <Icon size={16} />
    </span>
    <span className="flex-1 text-sm font-black tracking-tight leading-tight">
      {label}
    </span>
    <ChevronRight size={14} className={active ? 'text-[#7E22CE]' : 'text-slate-400'} />
  </Link>
);

// Anchor-row uses a <button> so `navigateExplore` can smooth-scroll on the
// homepage instead of triggering a full router change.
const AnchorRow = ({ item, onClick }) => {
  const Icon = item.Icon;
  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full flex items-center gap-3 px-4 min-h-[52px] rounded-lg transition-colors border bg-white border-transparent text-slate-800 hover:bg-slate-50 hover:border-slate-200 text-left"
      data-testid={item.testId}
    >
      <span className="shrink-0 w-9 h-9 rounded-full border flex items-center justify-center bg-[#FBF9FF] border-[#EDE9FE] text-[#7E22CE]">
        <Icon size={16} />
      </span>
      <span className="flex-1 text-sm font-black tracking-tight leading-tight">
        {item.label}
      </span>
      {item.badge && (
        <span className="text-[9px] font-black uppercase tracking-widest px-1.5 py-0.5 rounded-full bg-[#7C3AED] text-white mr-1">
          {item.badge}
        </span>
      )}
      <ChevronRight size={14} className="text-slate-400" />
    </button>
  );
};

const SectionLabel = ({ children }) => (
  <div className="text-[10px] uppercase tracking-widest text-[#A855F7] font-black mt-5 mb-2 px-1">
    {children}
  </div>
);

export const MobileNavDrawer = ({ user, onLogout }) => {
  const { visible: aiVisible } = useAIFormCheckVisibility();
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  // Close automatically on route change so tapping any nav row dismisses
  // the drawer without leaving stale scroll state behind. We watch `hash`
  // too so same-page anchor taps also close the drawer.
  useEffect(() => {
    setOpen(false);
  }, [location.pathname, location.search, location.hash]);

  const close = () => setOpen(false);

  const handleExplore = (target) => () => {
    navigateExplore(target, {
      navigate,
      currentPathname: location.pathname,
      onClose: close,
    });
  };

  const handleSignIn = () => {
    close();
    saveReturnTo();
    navigate('/login');
  };
  const handleJoin = () => {
    close();
    saveReturnTo();
    navigate('/register');
  };
  const handleLogout = () => {
    close();
    onLogout?.();
    navigate('/');
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <button
          type="button"
          className="lg:hidden inline-flex items-center justify-center w-10 h-10 rounded-md border border-slate-200 bg-white text-slate-800 hover:bg-slate-50 transition-colors shrink-0"
          aria-label="Open navigation menu"
          data-testid="mobile-nav-trigger"
        >
          <Menu size={20} />
        </button>
      </SheetTrigger>
      <SheetContent
        side="left"
        className="p-0 w-[86%] max-w-sm flex flex-col"
        data-testid="mobile-nav-drawer"
      >
        <SheetHeader className="px-5 pt-5 pb-3 border-b border-[#F1EEF7]">
          <SheetTitle className="flex items-center gap-2 text-[#3F2774]">
            <img
              src="/lgra-logo.webp"
              alt="LGRA Academy"
              className="h-8 w-auto"
            />
            <span className="font-black tracking-tight text-base">LGRA Academy</span>
          </SheetTitle>
          {user && (
            <div
              className="mt-2 rounded-md bg-gradient-to-br from-[#F5F3FF] to-white border border-[#EDE9FE] px-3 py-2 text-left"
              data-testid="mobile-nav-user-strip"
            >
              <div className="text-[10px] font-black uppercase tracking-widest text-[#A855F7]">
                Signed in
              </div>
              <div className="text-sm font-black text-[#101B3A] truncate">
                {user.name || user.email || 'My Account'}
              </div>
            </div>
          )}
        </SheetHeader>

        <nav className="flex-1 overflow-y-auto px-3 pb-6" aria-label="Mobile navigation">
          {/* ── Explore LGRA quick-nav (grouped anchor sections) ── */}
          <SectionLabel>Start Here</SectionLabel>
          <div className="space-y-1.5" data-testid="mnav-group-start-here">
            {START_HERE.map((item) => (
              <AnchorRow key={item.to} item={item} onClick={handleExplore(item.to)} />
            ))}
          </div>

          <SectionLabel>Training Options</SectionLabel>
          <div className="space-y-1.5" data-testid="mnav-group-training-options">
            {TRAINING_OPTIONS.filter((item) => aiVisible || item.to !== '/ai-running-form-check').map((item) => (
              <AnchorRow key={item.to} item={item} onClick={handleExplore(item.to)} />
            ))}
          </div>

          <SectionLabel>Explore LGRA</SectionLabel>
          <div className="space-y-1.5" data-testid="mnav-group-explore-more">
            {EXPLORE_MORE.map((item) => (
              <AnchorRow key={item.to} item={item} onClick={handleExplore(item.to)} />
            ))}
          </div>

          {/* ── Full page shortcuts ── */}
          <SectionLabel>Full Site</SectionLabel>
          <div className="space-y-1.5">
            {SITE_ITEMS.map((item) => (
              <NavRow
                key={item.to}
                {...item}
                active={item.match(location.pathname)}
                onClick={close}
              />
            ))}
          </div>

          <SectionLabel>Classes &amp; Purchase</SectionLabel>
          <div className="space-y-1.5">
            {CATEGORY_ITEMS.map((item) => (
              <NavRow
                key={item.testId}
                to={item.to}
                label={item.label}
                icon={item.icon}
                testId={item.testId}
                active={isCategoryActive(location.pathname, location.search, item.matchKeys)}
                onClick={close}
              />
            ))}
          </div>

          {user && (
            <>
              <SectionLabel>My LGRA</SectionLabel>
              <div className="space-y-1.5">
                {AUTH_ITEMS.map((item) => (
                  <NavRow
                    key={item.to}
                    {...item}
                    active={location.pathname + location.search === item.to || location.pathname === item.to.split('?')[0]}
                    onClick={close}
                  />
                ))}
              </div>
            </>
          )}

          {/* Signed-out CTAs — placed at the bottom so returning members can
              find them without scrolling past every category. */}
          {!user && (
            <div className="mt-6 grid grid-cols-2 gap-2" data-testid="mobile-nav-auth-ctas">
              <button
                type="button"
                onClick={handleSignIn}
                className="inline-flex items-center justify-center gap-1.5 h-11 rounded-md border-2 border-[#5530B5] text-[#5530B5] hover:bg-[#F3EAFF] font-black uppercase tracking-widest text-[11px] transition-colors"
                data-testid="mobile-nav-signin"
              >
                <LogIn size={14} /> Sign In
              </button>
              <button
                type="button"
                onClick={handleJoin}
                className="inline-flex items-center justify-center gap-1.5 h-11 rounded-md bg-[#5530B5] hover:bg-[#3F2774] text-white font-black uppercase tracking-widest text-[11px] transition-colors"
                data-testid="mobile-nav-join"
              >
                <UserPlus size={14} /> Join Now
              </button>
            </div>
          )}
        </nav>

        {user && (
          <div className="border-t border-[#F1EEF7] px-3 py-3 bg-white">
            <button
              type="button"
              onClick={handleLogout}
              className="w-full inline-flex items-center justify-center gap-2 h-11 rounded-md border border-red-200 text-red-700 hover:bg-red-50 font-black uppercase tracking-widest text-[11px] transition-colors"
              data-testid="mobile-nav-logout"
            >
              <LogOut size={14} /> Log Out
            </button>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};

export default MobileNavDrawer;
